// src/pages/CitizenReports.tsx
import React, { useState, useEffect } from 'react';
import {
  Filter,
  Download,
  Search,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  FileText,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AttachmentPreview from '../components/AttachementPrieview';
import { apiService, API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';

const CitizenReports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    dateRange: '7days',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchMyReports();
  }, []);

  const fetchMyReports = async () => {
    try {
      setError(null);
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expired. Please login again.');
        return;
      }

      // Method 1: Use the authenticated /my-reports endpoint (works with email login)
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/my-reports`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setReports(data.data || []);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('my-reports failed, trying phone number fallback:', err);
      }

      // Method 2: Fallback — phone number from stored citizen profile
      const citizenStr = localStorage.getItem('citizen');
      if (citizenStr) {
        const citizen = JSON.parse(citizenStr);
        const phoneNumber = citizen.phoneNumber || citizen.phone_number;
        if (phoneNumber) {
          const response = await apiService.getCitizenReports(phoneNumber);
          if (response.data.success) {
            setReports(response.data.data || []);
            setLoading(false);
            return;
          }
        }
      }

      setReports([]);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else if (error.response?.status === 404) {
        setReports([]);
      } else {
        setError('Failed to load reports. Please try again.');
        setReports([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => toast.success('Export feature coming soon!');
  const handleNewReport = () => navigate('/citizen/report/new');
  const handleViewReport = (reportId: string) => navigate(`/citizen/reports/${reportId}`);

  const getStatusCount = (status: string) => reports.filter(r => r.status === status).length;

  const getUniqueWardsCount = () => {
    const seen: Record<string, boolean> = {};
    reports.forEach(r => { if (r.wardId != null) seen[r.wardId] = true; });
    return Object.keys(seen).length;
  };

  const filteredReports = reports.filter(report => {
    if (filters.status !== 'all' && report.status !== filters.status) return false;
    if (filters.category !== 'all' && report.category !== filters.category) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      return (
        report.description?.toLowerCase().includes(q) ||
        report.reportId?.toLowerCase().includes(q) ||
        report.category?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return <CheckCircle className="text-[#3c8d2e]" size={16} />;
      case 'IN_PROGRESS': return <Clock className="text-[#0b2045]" size={16} />;
      case 'PENDING': return <Clock className="text-[#ca8a04]" size={16} />;
      case 'URGENT': return <AlertTriangle className="text-red-500" size={16} />;
      default: return <Clock className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return 'bg-[#3c8d2e]/10 text-[#3c8d2e]';
      case 'IN_PROGRESS': return 'bg-[#0b2045]/10 text-[#0b2045]';
      case 'PENDING': return 'bg-[#fefce8] text-[#ca8a04]';
      case 'URGENT': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f4f7f6]">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#0b2045]" size={40} />
          <p className="text-gray-600">Loading your reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
        <h3 className="text-lg font-medium text-[#0b2045] mb-2">{error}</h3>
        <button onClick={fetchMyReports} className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] transition-colors">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button onClick={() => navigate('/citizen/dashboard')} className="flex items-center text-gray-600 hover:text-[#0b2045] mb-2 transition-colors">
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-[#0b2045]">My Reports</h1>
          <p className="text-gray-600">Track and manage all your submitted reports</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-[#f4f7f6] transition-colors">
            <Filter size={16} className="mr-2" />Filters
          </button>
          <button onClick={handleNewReport} className="inline-flex items-center px-4 py-2 bg-[#3c8d2e] text-white rounded-lg text-sm font-medium hover:bg-[#2d7022] transition-colors">
            <FileText size={16} className="mr-2" />New Report
          </button>
          <button onClick={fetchMyReports} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-[#f4f7f6] transition-colors">
            <RefreshCw size={16} className="mr-2" />Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Reports', value: reports.length, color: 'border-[#0b2045]', icon: <FileText className="text-[#0b2045]" size={20} />, bg: 'bg-[#0b2045]/10' },
          { label: 'Pending', value: getStatusCount('PENDING'), color: 'border-[#ca8a04]', icon: <Clock className="text-[#ca8a04]" size={20} />, bg: 'bg-[#fefce8]' },
          { label: 'Resolved', value: getStatusCount('RESOLVED'), color: 'border-[#3c8d2e]', icon: <CheckCircle className="text-[#3c8d2e]" size={20} />, bg: 'bg-[#3c8d2e]/10' },
          { label: 'Wards', value: getUniqueWardsCount(), color: 'border-[#0b2045]', icon: <MapPin className="text-[#0b2045]" size={20} />, bg: 'bg-[#0b2045]/10' },
        ].map((stat, i) => (
          <div key={i} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${stat.color}`}>
            <div className="flex items-center">
              <div className={`p-2 ${stat.bg} rounded-lg mr-3`}>{stat.icon}</div>
              <div>
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-xl font-bold text-[#0b2045]">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="all">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}>
                <option value="all">All Categories</option>
                <option value="WATER">Water Supply</option>
                <option value="ROADS">Roads & Infrastructure</option>
                <option value="HEALTH">Health Services</option>
                <option value="EDUCATION">Education</option>
                <option value="SECURITY">Security</option>
                <option value="SANITATION">Sanitation & Waste</option>
                <option value="ELECTRICITY">Electricity</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent" value={filters.dateRange} onChange={e => setFilters({ ...filters, dateRange: e.target.value })}>
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="90days">Last 90 days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-[#0b2045]/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="search"
            placeholder="Search your reports by description or category..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-[#0b2045]/10">
        {filteredReports.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-[#0b2045] mb-2">
              {filters.status !== 'all' || filters.category !== 'all' || filters.search
                ? 'No reports match your filters'
                : 'No reports yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {filters.status !== 'all' || filters.category !== 'all' || filters.search
                ? 'Try adjusting your filters'
                : 'Submit your first report to get started'}
            </p>
            <button onClick={handleNewReport} className="px-6 py-3 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] font-medium transition-colors">
              Submit New Report
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#f4f7f6]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#0b2045] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map(report => (
                  <tr key={report.id} className="hover:bg-[#f4f7f6] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-[#0b2045]">{report.reportId}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[#0b2045]">{report.description}</div>
                      <div className="text-xs text-gray-500">
                        <MapPin size={12} className="inline mr-1" />
                        {report.wardName || report.constituencyName || 'No location specified'}
                      </div>
                      {report.attachments && report.attachments.length > 0 && (
                        <AttachmentPreview reportId={report.reportId || report.id} attachments={report.attachments} />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0b2045]/10 text-[#0b2045]">
                        {report.categoryDisplayName || report.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(report.status)}
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-[#0b2045]">
                        {new Date(report.reportedAt || report.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button onClick={() => handleViewReport(report.id)} className="text-[#3c8d2e] hover:text-[#2d7022] text-sm font-medium transition-colors">
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Guide */}
      <div className="bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-xl p-4">
        <div className="flex items-start">
          <AlertTriangle className="text-[#0b2045] mr-3 mt-0.5" size={20} />
          <div>
            <h3 className="text-sm font-medium text-[#0b2045]">Report Status Guide</h3>
            <ul className="mt-2 text-sm text-[#0b2045]/80 space-y-1">
              <li>• <strong>Pending:</strong> Your report has been received and is awaiting review</li>
              <li>• <strong>In Progress:</strong> Authorities are working on resolving the issue</li>
              <li>• <strong>Resolved:</strong> The issue has been successfully addressed</li>
            </ul>
          </div>
        </div>
      </div>

      {filteredReports.length > 0 && (
        <div className="flex justify-end">
          <button onClick={handleExport} className="inline-flex items-center px-5 py-2.5 bg-[#0b2045] text-white rounded-lg text-sm font-medium hover:bg-[#152c53] transition-colors">
            <Download size={16} className="mr-2" />
            Export Reports (CSV)
          </button>
        </div>
      )}
    </div>
  );
};

export default CitizenReports;